// Draggable centre line for the admin dashboard: left = child (Balagruha / students) view,
// right = coach view. Drag to resize, drag to an edge (or use the arrow buttons) to minimise a view,
// double-click to reset. The position is remembered in this browser.
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./AdminSplitHandle.css";

const STORAGE_KEY = "admin-dashboard-split";
export const DEFAULT_SPLIT = 40; // % width of the left (child) view
const SNAP = 8; // within this many % of an edge, collapse that view completely
const MIN_OPEN = 20; // smallest width a view keeps while it's open

const clampSplit = (pct) => {
  if (pct <= SNAP) return 0;
  if (pct >= 100 - SNAP) return 100;
  return Math.min(100 - MIN_OPEN, Math.max(MIN_OPEN, Math.round(pct)));
};

const readStored = () => {
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(v) && v >= 0 && v <= 100 && localStorage.getItem(STORAGE_KEY) !== null ? v : DEFAULT_SPLIT;
  } catch {
    return DEFAULT_SPLIT;
  }
};

// Left-panel width in % plus a setter that also remembers the value.
export function useAdminSplit() {
  const [split, setSplitState] = useState(readStored);
  const setSplit = useCallback((pct) => {
    const next = clampSplit(pct);
    setSplitState(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* storage unavailable */ }
  }, []);
  return [split, setSplit];
}

export default function AdminSplitHandle({ containerRef, split, onChange, leftLabel = "Child view", rightLabel = "Coach view" }) {
  const [dragging, setDragging] = useState(false);
  // Width to restore when a minimised view is reopened. Not tracked mid-drag, otherwise dragging
  // to an edge would remember the narrow widths passed on the way.
  const lastOpen = useRef(split > 0 && split < 100 ? split : DEFAULT_SPLIT);
  const dragStart = useRef(null);
  useEffect(() => { if (!dragging && split > 0 && split < 100) lastOpen.current = split; }, [split, dragging]);

  const pctFromEvent = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return split;
    return ((e.clientX - rect.left) / rect.width) * 100;
  }, [containerRef, split]);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => onChange(pctFromEvent(e));
    const up = () => {
      // Dragged to an edge: reopening should go back to where the drag started.
      if (dragStart.current !== null) lastOpen.current = dragStart.current;
      dragStart.current = null;
      setDragging(false);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.body.classList.add("admin-split-dragging");
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.body.classList.remove("admin-split-dragging");
    };
  }, [dragging, onChange, pctFromEvent]);

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 5;
    if (e.key === "ArrowLeft") { e.preventDefault(); onChange(split - step); }
    else if (e.key === "ArrowRight") { e.preventDefault(); onChange(split + step); }
    else if (e.key === "Home") { e.preventDefault(); onChange(0); }
    else if (e.key === "End") { e.preventDefault(); onChange(100); }
    else if (e.key === "Enter") { e.preventDefault(); onChange(DEFAULT_SPLIT); }
  };

  // Arrow buttons: collapse the view on that side, or bring it back if it's collapsed.
  const towardsLeft = () => onChange(split >= 100 ? lastOpen.current : 0);
  const towardsRight = () => onChange(split <= 0 ? lastOpen.current : 100);

  return (
    <div
      className={`admin-split-handle${dragging ? " is-dragging" : ""}${split <= 0 ? " is-left-closed" : ""}${split >= 100 ? " is-right-closed" : ""}`}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${leftLabel} and ${rightLabel}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={split}
      aria-valuetext={split <= 0 ? `${leftLabel} minimised` : split >= 100 ? `${rightLabel} minimised` : `${leftLabel} ${split}%`}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={(e) => {
        if (e.button !== 0 || e.target.closest("button")) return;
        e.preventDefault();
        dragStart.current = split > 0 && split < 100 ? split : lastOpen.current;
        setDragging(true);
      }}
      onDoubleClick={(e) => { if (!e.target.closest("button")) onChange(DEFAULT_SPLIT); }}
      onKeyDown={onKeyDown}
    >
      <span className="admin-split-handle__grip" aria-hidden="true" />
      <div className="admin-split-handle__buttons">
        <button
          type="button"
          onClick={towardsLeft}
          disabled={split <= 0}
          title={split >= 100 ? `Show ${rightLabel}` : `Minimise ${leftLabel}`}
          aria-label={split >= 100 ? `Show ${rightLabel}` : `Minimise ${leftLabel}`}
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={towardsRight}
          disabled={split >= 100}
          title={split <= 0 ? `Show ${leftLabel}` : `Minimise ${rightLabel}`}
          aria-label={split <= 0 ? `Show ${leftLabel}` : `Minimise ${rightLabel}`}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
